import type { ComputedRef, Ref } from 'vue';
import { cartItemCount } from '~/composables/useCart';
import { useLocalStorage } from '@vueuse/core';
const route = useRoute();

const openNavItems = useLocalStorage('openNavItems', {
  reviewShield: false,
  reviewResponder: false,
  reviewBooster: false,
  settings: false,
  profile: false,
  admin: false,
});

export interface NavItem {
  label: string;
  icon: string;
  to?: string;
  slot?: string;
  settings?: string;
  badge?: Ref<number> | ComputedRef<number>;
  childrenOpen?: Ref<boolean>;
  children?: NavItem[];
}

export interface BreadcrumbItem {
  label: string;
  icon?: string;
  to?: string;
}

export function useNavigation() {
  const { data: userSession } = useAuth();
  // no shop in the native apps: subscriptions are not sold in-app (App Store / Google Play payment policies)
  const isNative = useIsNativePlatform();

  const navItems = computed<NavItem[]>(() => {
    const isEmployee = userSession.value?.roles.includes('ROLE_ORG_SCANNER') && !userSession.value?.roles.includes('ROLE_ORG_ADMIN');
    const isSuperAdmin = userSession.value?.roles.includes('ROLE_SUPER_ADMIN');

    const items: NavItem[] = isEmployee
      ? [
          { label: 'nav.main.reviewBooster.scanner', icon: 'heroicons:qr-code', to: '/review-booster/scanner' },
          { label: 'nav.main.profile.deleteAccount', icon: 'heroicons:user-minus', to: '/profile/delete-account' },
        ]
      : [
          { label: 'nav.main.home', icon: 'i-heroicons-home', to: '/' },
          ...(isNative
            ? []
            : [
                {
                  label: 'nav.main.shop',
                  icon: 'heroicons:shopping-bag',
                  to: '/shop',
                  slot: 'shop',
                  badge: cartItemCount,
                },
              ]),
          {
            label: 'nav.main.reviews',
            icon: 'heroicons:sparkles-solid',
            to: '/reviews',
          },
          {
            label: 'nav.main.reviewShield',
            icon: 'heroicons:shield-exclamation',
            to: '/review-shield',
          },
          {
            label: 'nav.main.reviewBooster',
            icon: 'heroicons:rocket-launch',
            settings: '/settings/review-booster',
            childrenOpen: computed({
              get: () => openNavItems.value.reviewBooster,
              set: (val) => {
                openNavItems.value.reviewBooster = val;
              },
            }),
            children: [
              {
                label: 'nav.main.reviewBooster.feedback',
                icon: 'heroicons:chat-bubble-left-right',
                to: '/review-booster/reviews',
              },
              {
                label: 'nav.main.reviewBooster.codes',
                icon: 'heroicons:ticket',
                to: '/review-booster/codes',
              },
              {
                label: 'nav.main.reviewBooster.scanner',
                icon: 'heroicons:qr-code',
                to: '/review-booster/scanner',
              },
              {
                label: 'nav.main.reviewBooster.statistics',
                icon: 'heroicons:chart-bar',
                to: '/review-booster/statistics',
              },
            ],
          },
          {
            label: 'nav.main.administration',
            icon: 'heroicons:cog-6-tooth',
            childrenOpen: computed({
              get: () => openNavItems.value.admin,
              set: (val) => {
                openNavItems.value.admin = val;
              },
            }),
            children: [
              {
                label: 'nav.main.settings.subscriptions',
                icon: 'ic:outline-feed',
                to: '/settings/subscriptions',
              },
              {
                label: 'nav.main.settings.invoiceRecipients',
                icon: 'ic:outline-payment',
                to: '/settings/customers',
              },
              {
                label: 'nav.main.settings.company',
                icon: 'heroicons:building-office-2',
                to: '/settings/organization',
              },
              {
                label: 'nav.main.settings.team',
                icon: 'heroicons:building-office-2',
                to: '/settings/team',
              },
            ],
          },
          {
            label: 'nav.main.settings.main',
            icon: 'i-heroicons-adjustments-horizontal',
            childrenOpen: computed({
              get: () => openNavItems.value.settings,
              set: (val) => {
                openNavItems.value.settings = val;
              },
            }),
            children: [
              {
                label: 'nav.main.settings.reviewResponder',
                icon: 'heroicons:sparkles-solid',
                to: '/settings/review-responder',
              },
              {
                label: 'nav.main.settings.reviewShield',
                icon: 'heroicons:shield-exclamation',
                to: '/settings/review-shield',
              },
              {
                label: 'nav.main.settings.reviewBooster',
                icon: 'heroicons:rocket-launch',
                to: '/settings/review-booster',
              },
              {
                label: 'nav.main.settings.googleLocations',
                icon: 'mdi:google',
                to: '/settings/google-account',
              },
            ],
          },
          {
            label: 'nav.main.profile.main',
            icon: 'heroicons:user-circle',
            childrenOpen: computed({
              get: () => openNavItems.value.profile,
              set: (val) => {
                openNavItems.value.profile = val;
              },
            }),
            children: [
              {
                label: 'nav.main.profile.account',
                icon: 'material-symbols:key-outline-rounded',
                to: '/profile/contact',
              },
              {
                label: 'nav.main.profile.notifications',
                icon: 'heroicons:bell-alert',
                to: '/profile/notifications',
              },
            ],
          },
        ];

    if (isSuperAdmin) {
      items.push({
        label: 'nav.main.admin',
        icon: 'heroicons:shield-check',
        to: '/admin',
      });
    }

    return items;
  });

  const getBreadcrumbs = (path: string): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [];

    for (const item of navItems.value) {
      if (item.to === path) {
        breadcrumbs.push({ label: item.label, icon: item.icon, to: item.to });
        return breadcrumbs;
      }

      if (item.children) {
        for (const child of item.children) {
          if (child.to === path) {
            breadcrumbs.push({ label: item.label, icon: item.icon });
            breadcrumbs.push({ label: child.label, icon: child.icon, to: child.to });
            return breadcrumbs;
          }
        }
      }
    }

    return breadcrumbs;
  };

  const currentRoutSettingsPath = computed<string | null>(() => {
    const currentPath = route.path;
    for (const item of navItems.value) {
      if (item.to === currentPath) {
        return item.settings ?? null;
      }
      if (item.children) {
        const child = item.children.find((c) => c.to === currentPath);
        if (child) {
          return child.settings ?? item.settings ?? null;
        }
      }
    }
    return null;
  });

  return {
    navItems,
    getBreadcrumbs,
    currentRoutSettingsPath,
  };
}
